import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from "axios";

enum StatusCode {
  Unauthorized = 401,
  Forbiden = 403,
  TooManyRequests = 429,
  InternalServerError = 500,
  MovedPermanently = 301,
}

const headers: Readonly<Record<string, string | boolean>> = {
  Accept: "application/json",
  "Content-Type": "application/json;",
  "X-requested-With": "XMLHttpRequest",
};
const publicPaths = ["/refund_preload/"];

function isPublicRequest(url?: string): boolean {
  if (!url) return false;
  const path = url.split("?")[0];
  return publicPaths.some((publicPath) => path.includes(publicPath));
}

const injectToken = (config: AxiosRequestConfig | any): AxiosRequestConfig => {
  try {
    const token = localStorage.getItem("token");
    if (token && !isPublicRequest(config.url)) {
      config.headers["Authorization"] = `JWT ${token}`;
    }
    return config;
  } catch (error: any) {
    throw new Error(error);
  }
};

class Http {
  private instance: AxiosInstance | null = null;

  private get http(): AxiosInstance {
    return this.instance != null ? this.instance : this.initHttp();
  }

  initHttp() {
    const http = axios.create({
      baseURL: import.meta.env.VITE_BASE_URL,
      headers,
    });

    http.interceptors.request.use(injectToken, (error) =>
      Promise.reject(error),
    );

    http.interceptors.response.use(
      (response) => response,
      (error) => {
        const { response } = error;
        return this.handleError(response);
      },
    );

    this.instance = http;
    return http;
  }

  request<T = any, R = AxiosResponse<T>>(
    config: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.request(config);
  }

  get<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.get<T, R>(url, config);
  }

  post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.post<T, R>(url, data, config);
  }

  put<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.put<T, R>(url, data, config);
  }

  patch<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.patch<T, R>(url, data, config);
  }

  delete<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return this.http.delete<T, R>(url, config);
  }

  private handleError(error: any) {
    const { status } = error;

    switch (status) {
      case StatusCode.InternalServerError: {
        break;
      }
      case StatusCode.Forbiden: {
        break;
      }
      case StatusCode.Unauthorized: {
        localStorage.clear();
        localStorage.setItem("sesionExpired", "true");
        location.replace("/login");
        // Handle Unauthorized
        break;
      }
      case StatusCode.TooManyRequests: {
        // Handle TooManyRequests
        break;
      }
      case StatusCode.MovedPermanently: {
        break;
      }
      default:
        break;
    }

    return Promise.reject(error);
  }
}

export const axiosInstance = new Http();
